// src/store/modules/productImages.js
// State for the product image library: list, upload (single and bulk),
// rename, delete, bulk delete.
//
// Structurally the same module as store/modules/logos.js. The one
// addition is uploadImages(), which is what makes the library the main
// route rather than a one-at-a-time picker.

import api from '@/services/api'

// How many uploads run at once. Three keeps a folder of forty moving
// without saturating a kitchen's connection or opening forty sockets
// against a Plesk-fronted nginx.
const UPLOAD_CONCURRENCY = 3

export default {
  namespaced: true,

  state: () => ({
    images:  [],
    loading: false,
    error:   null,
    // Per-file progress for a bulk upload, so the UI can show which
    // file is on the wire and which one failed. Cleared when a new
    // batch starts, NOT when it finishes — the user needs to read the
    // failures after it is done.
    uploads: [],
  }),

  getters: {
    images:  (s) => s.images,
    loading: (s) => s.loading,
    error:   (s) => s.error,
    uploads: (s) => s.uploads,
    byId:    (s) => (id) => s.images.find(i => i.imageId === id) || null,
    uploading: (s) => s.uploads.some(u => u.status === 'pending' || u.status === 'uploading'),
  },

  mutations: {
    SET_IMAGES(state, images) { state.images = images },
    ADD_IMAGE(state, image)   { state.images.unshift(image) },
    UPDATE_IMAGE(state, image) {
      const idx = state.images.findIndex(i => i.imageId === image.imageId)
      if (idx >= 0) state.images.splice(idx, 1, image)
    },
    REMOVE_IMAGE(state, imageId) {
      state.images = state.images.filter(i => i.imageId !== imageId)
    },
    REMOVE_IMAGES(state, imageIds) {
      const set = new Set(imageIds)
      state.images = state.images.filter(i => !set.has(i.imageId))
    },
    SET_LOADING(state, val) { state.loading = val },
    SET_ERROR(state, msg)   { state.error   = msg },

    SET_UPLOADS(state, uploads) { state.uploads = uploads },
    UPDATE_UPLOAD(state, { index, patch }) {
      const current = state.uploads[index]
      if (current) state.uploads.splice(index, 1, { ...current, ...patch })
    },
    CLEAR_UPLOADS(state) { state.uploads = [] },
  },

  actions: {
    async fetchImages({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/designer/images')
        commit('SET_IMAGES', data.images)
        return data.images
      } catch (err) {
        commit('SET_ERROR', err.response?.data?.error || 'Failed to load images')
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Make sure the list is loaded, without re-fetching if it already is.
     * Used by the product dialog, which needs to resolve an imageId to a
     * thumbnail but has no reason to hit the API every time it opens.
     */
    async ensureImages({ state, dispatch }) {
      if (state.images.length > 0) return state.images
      return dispatch('fetchImages')
    },

    /**
     * Upload one image. Resizing happens in the browser first — see
     * src/utils/imageResize.js for why it is client-side and what the
     * EXIF and transparency traps are.
     *
     * A server-side duplicate (same bytes already in this customer's
     * library) comes back as { duplicate: true } with the existing row.
     * That is a success, not an error: the image the user wanted is in
     * the library. It is simply not added to the list twice.
     */
    async uploadImage({ commit, state }, file) {
      try {
        const { buildProductImageUpload } = await import('@/utils/imageResize')
        const formData = await buildProductImageUpload(file)

        const { data } = await api.post('/designer/images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        const known = state.images.some(i => i.imageId === data.image.imageId)
        if (!known) commit('ADD_IMAGE', data.image)

        return { ok: true, image: data.image, duplicate: !!data.duplicate }
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Upload failed'
        return { ok: false, error: msg }
      }
    },

    /**
     * Upload many images at once — the point of the library.
     *
     * One request per file with a small concurrency limit, rather than
     * one request carrying forty files. A corrupt or oversized file
     * then fails on its own and the rest still land, the same reasoning
     * behind one transaction per row in /api/fooditems/bulk. It also
     * gives honest per-file progress, which a single large multipart
     * request cannot.
     *
     * Never rejects: the result carries the per-file outcome. A thrown
     * error halfway through a batch would leave the caller unable to
     * tell which files made it.
     */
    async uploadImages({ commit, dispatch }, files) {
      const list = Array.from(files || [])
      if (list.length === 0) return { ok: true, uploaded: 0, failed: 0, results: [] }

      commit('SET_UPLOADS', list.map(f => ({
        name:   f.name,
        status: 'pending',
        error:  null,
      })))

      const results = new Array(list.length)
      let cursor = 0

      const worker = async () => {
        while (cursor < list.length) {
          const index = cursor++
          commit('UPDATE_UPLOAD', { index, patch: { status: 'uploading' } })

          const result = await dispatch('uploadImage', list[index])
          results[index] = { name: list[index].name, ...result }

          commit('UPDATE_UPLOAD', {
            index,
            patch: {
              status: result.ok ? (result.duplicate ? 'duplicate' : 'done') : 'failed',
              error:  result.ok ? null : result.error,
            },
          })
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, list.length) }, worker)
      )

      const failed = results.filter(r => !r.ok).length
      return {
        ok:        failed === 0,
        uploaded:  results.filter(r => r.ok && !r.duplicate).length,
        duplicates: results.filter(r => r.ok && r.duplicate).length,
        failed,
        results,
      }
    },

    /**
     * Rename an image. Backend uses PUT for single-resource updates
     * per project convention.
     */
    async renameImage({ commit }, { imageId, imageName }) {
      try {
        const { data } = await api.put(`/designer/images/${imageId}`, { imageName })
        commit('UPDATE_IMAGE', data.image)
        return { ok: true, image: data.image }
      } catch (err) {
        const msg = err.response?.data?.error || 'Rename failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    /**
     * Delete a single image. The backend also NULLs out foodItems.imageId
     * for any product using it — those products keep working, they just
     * lose their picture until one is reassigned.
     */
    async deleteImage({ commit }, imageId) {
      try {
        await api.delete(`/designer/images/${imageId}`)
        commit('REMOVE_IMAGE', imageId)
        return { ok: true }
      } catch (err) {
        const msg = err.response?.data?.error || 'Delete failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    /**
     * Delete many images at once. Returns { ok, deleted, missing }.
     */
    async bulkDeleteImages({ commit }, imageIds) {
      try {
        const { data } = await api.post('/designer/images/bulk-delete', { imageIds })
        // Only remove the IDs the backend actually confirmed as deleted,
        // so state stays consistent if some were already gone.
        commit('REMOVE_IMAGES', data.deletedIds || [])
        return {
          ok:      true,
          deleted: data.deleted || 0,
          missing: data.missing || 0,
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Bulk delete failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    clearUploads({ commit }) {
      commit('CLEAR_UPLOADS')
    },
  },
}